import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Btn, FormField, toast } from '../components/UI';
import { CheckCircle, AlertCircle, ArrowRight, Users, Building2 } from 'lucide-react';
import { validatePassword } from '../utils/validators';

const ROLE_LABELS = { admin: 'Administrador', client: 'Cliente', professional: 'Profesional sanitario', ambulance: 'Recurso / Ambulancia' };

export default function InvitePage({ token }) {
  const { reloadUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ password: '', password2: '', first_name: '', last_name: '', phone: '', specialization: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getInvitation(token)
      .then(inv => {
        setInvite(inv);
        setForm(f => ({
          ...f,
          first_name: inv.first_name || '',
          last_name: inv.last_name || '',
          specialization: inv.specialization || 'TES',
        }));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.password || form.password !== form.password2) {
      setError('Las contraseñas no coinciden');
      return;
    }
    const pwCheck = validatePassword(form.password);
    if (!pwCheck.valid) {
      setError('Contraseña débil: ' + pwCheck.errors.join(', '));
      return;
    }
    setSubmitting(true);
    try {
      await api.acceptInvitation({
        token,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        specialization: form.specialization,
      });
      toast.success('Cuenta creada', `Bienvenido/a a ${invite.tenant_name}`);
      // Clear ?token from URL and refresh auth state
      window.history.replaceState({}, '', '/');
      await reloadUser();
      window.location.reload();
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (loading) {
    return <CenterWrap><div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Cargando invitación...</div></CenterWrap>;
  }

  if (error && !invite) {
    return (
      <CenterWrap>
        <div style={{ textAlign: 'center' }}>
          <AlertCircle size={48} style={{ color: '#EF4444', marginBottom: 12 }} />
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Invitación inválida</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20, maxWidth: 400 }}>{error}</p>
          <a href="/" style={{ color: 'var(--accent)', fontSize: 13, fontWeight: 600 }}>Volver al login</a>
        </div>
      </CenterWrap>
    );
  }

  return (
    <CenterWrap>
      <div style={{ maxWidth: 480, width: '100%', background: 'var(--card)', borderRadius: 16, padding: 32, border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, var(--accent), #8B5CF6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={22} style={{ color: '#fff' }} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Invitación recibida</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{invite.tenant_name}</h2>
          </div>
        </div>

        <div style={{ padding: '12px 14px', background: 'var(--surface-alt)', borderRadius: 10, marginBottom: 16, fontSize: 13, lineHeight: 1.6 }}>
          <strong>{invite.invited_by || 'El equipo'}</strong> te ha invitado a unirte a <strong>{invite.tenant_name}</strong> como <strong>{ROLE_LABELS[invite.role] || invite.role}</strong>.
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Email: <strong style={{ fontFamily: 'var(--font-mono)' }}>{invite.email}</strong></div>
          {invite.company_name && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}><Building2 size={11} style={{ verticalAlign: 'middle' }} /> {invite.company_name}</div>}
        </div>

        {invite.message && (
          <div style={{ padding: '10px 14px', background: '#3B82F610', borderLeft: '3px solid #3B82F6', borderRadius: '0 8px 8px 0', marginBottom: 16, fontSize: 13, fontStyle: 'italic' }}>
            "{invite.message}"
          </div>
        )}

        {error && <div style={{ padding: '10px 14px', background: 'var(--warn-dim)', borderRadius: 8, color: 'var(--warn)', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><AlertCircle size={16} />{error}</div>}

        <form onSubmit={submit}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 10 }}>Completa tu perfil</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Nombre"><input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} autoFocus /></FormField>
            <FormField label="Apellidos"><input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} /></FormField>
          </div>
          <FormField label="Teléfono (opcional)">
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+34 600 000 000" />
          </FormField>
          {invite.role === 'professional' && (
            <FormField label="Especialización">
              <select value={form.specialization} onChange={e => setForm({ ...form, specialization: e.target.value })}>
                <option value="TES">TES</option>
                <option value="medico_sva">Médico/a</option>
                <option value="enfermero">Enfermero/a</option>
                <option value="supervisor">Supervisor/a</option>
                <option value="conductor">Conductor</option>
              </select>
            </FormField>
          )}

          <div style={{ fontSize: 12, fontWeight: 700, color: '#8B5CF6', textTransform: 'uppercase', marginTop: 14, marginBottom: 10 }}>Elige tu contraseña</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Contraseña">
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
            </FormField>
            <FormField label="Confirmar">
              <input type="password" value={form.password2} onChange={e => setForm({ ...form, password2: e.target.value })} autoComplete="new-password" />
            </FormField>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>Mínimo 8 chars con mayúscula, minúscula, número y símbolo.</div>

          <Btn type="submit" icon={ArrowRight} disabled={submitting} style={{ width: '100%', justifyContent: 'center' }}>
            {submitting ? 'Creando cuenta...' : 'Aceptar invitación y entrar'}
          </Btn>
        </form>

        <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: 'var(--text-dim)' }}>
          Expira el {new Date(invite.expires_at).toLocaleDateString('es-ES')}
        </div>
      </div>
    </CenterWrap>
  );
}

function CenterWrap({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
      {children}
    </div>
  );
}
